import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouteContext, useRouter } from "@tanstack/react-router";

import { fetchAuthenticatedData } from "@/queries/shared";

export type Role = {
  roleId: string;
  roleName: string;
};

export type UserOrg = {
  userOrgId: number;
  esiIdPath: string;
  esiId: number;
  cdsCode: string;
  orgName: string;
  vendorNumber: string;
  orgType: number;
  roleIds: number[];
};

export class UserOrgForm {
  userOrgId: string | undefined;
  esiIdPath: string;
  esiId: string;
  cdsCode: string;
  orgName: string;
  orgType: string;
  roleIds: string[];

  constructor(userOrg: UserOrg) {
    this.userOrgId = userOrg.userOrgId?.toString();
    this.esiIdPath = userOrg.esiIdPath;
    this.esiId = userOrg.esiId.toString();
    this.cdsCode = userOrg.cdsCode;
    this.orgName = userOrg.orgName;
    this.orgType = userOrg.orgType.toString();
    this.roleIds = userOrg.roleIds.map((x) => x.toString());
  }
}

// Equivalent to CAPSDAC.Application.API.Models.User
export type User<T> = {
  email: string;
  userName: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  phoneExtension: string;
  userOrgs: T[];
};

export function userMutationFunctions(
  leaId: string,
  handleCloseDialog: () => void
) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const queryClient = useQueryClient();
  const router = useRouter();
  const mutation = useMutation({
    mutationFn: ({
      method,
      urlVal,
      userData,
    }: {
      method: string;
      urlVal: string;
      userData: User<UserOrg | UserOrgForm>;
    }) => {
      return fetchAuthenticatedData<User<UserOrg | UserOrgForm>>(
        leaContext,
        urlVal,
        {
          method: method,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        }
      );
    },
    onSuccess: (_resp, _variables) => {
      queryClient.resetQueries({ queryKey: ["user"] });
      queryClient.resetQueries({ queryKey: ["login_detail"] });
      router.invalidate().then(() => handleCloseDialog());
    },
    onError: (error) => {
      console.error(error);
    },
  });
  return {
    mutationState: mutation,
    handleUserSave: async function (userData: User<UserOrg | UserOrgForm>) {
      var urlVal = `/json/account/${leaId}/users/${userData.userName}`;
      mutation.mutate({ method: "POST", urlVal, userData });
    },
    handleUserOrgSave: async function (userData: User<UserOrg | UserOrgForm>) {
      var urlVal = `/json/account/${leaId}/users/${userData.userName}/user-orgs`;
      mutation.mutate({ method: "POST", urlVal, userData });
    },
    handleUserOrgDelete: async function (
      userData: User<UserOrg | UserOrgForm>
    ) {
      var urlVal = `/json/account/${leaId}/users/${userData.userName}/user-orgs`;
      mutation.mutate({ method: "DELETE", urlVal, userData });
    },
  };
}
